import { useMemo } from 'react';
import Select from 'react-select';
import { Input } from 'reactstrap';
import { fmtTime } from '../Helpers/format';

export const DEFAULT_TIME = '12:00';
export const DEFAULT_ENTRY_END_TIME = '12:15';

const pad = (value) => String(value).padStart(2, '0');

export const TIME_OPTIONS = Array.from({ length: 24 * 4 }, (_, index) => {
  const minutesAfterMidnight = index * 15;
  const hours = Math.floor(minutesAfterMidnight / 60);
  const minutes = minutesAfterMidnight % 60;
  const value = `${pad(hours)}:${pad(minutes)}`;
  return { value, label: fmtTime(value) };
});

export const timeOptionFor = (value) => {
  const normalized = String(value || '').slice(0, 5);
  const standardOption = TIME_OPTIONS.find((option) => option.value === normalized);
  if (standardOption) return standardOption;

  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(normalized)) return null;
  return { value: normalized, label: fmtTime(normalized) };
};

const timeSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    minHeight: 34,
    borderColor: isFocused ? '#0e578e' : '#e9e9ef',
    boxShadow: isFocused ? '0 0 0 0.15rem rgba(14, 87, 142, 0.25)' : 'none',
    backgroundColor: isDisabled ? '#f8f9fa' : '#ffffff',
    fontSize: 13,
    '&:hover': { borderColor: isFocused ? '#0e578e' : '#ced4da' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 9px' }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 6 }),
  indicatorSeparator: () => ({ display: 'none' }),
};

/** Quarter-hour time picker that keeps the API value in 24-hour HH:mm format. */
const TimeSelect = ({
  inputId,
  value,
  onChange,
  ariaLabel,
  isDisabled = false,
  readOnly = false,
}) => {
  const selectedOption = useMemo(() => timeOptionFor(value || DEFAULT_TIME), [value]);

  if (readOnly) {
    return (
      <Input
        id={inputId}
        aria-label={ariaLabel}
        type="text"
        value={selectedOption?.label || ''}
        readOnly
        disabled={isDisabled}
      />
    );
  }

  return (
    <Select
      inputId={inputId}
      aria-label={ariaLabel}
      value={selectedOption}
      onChange={(option) => onChange(option?.value || DEFAULT_TIME)}
      options={TIME_OPTIONS}
      isDisabled={isDisabled}
      isClearable={false}
      isSearchable
      openMenuOnFocus
      menuPlacement="auto"
      menuPosition="fixed"
      menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
      maxMenuHeight={240}
      styles={timeSelectStyles}
      className="react-select-container pm-time-select"
      classNamePrefix="react-select"
    />
  );
};

export { TimeSelect };
export default TimeSelect;
